/**
 * PerformanceOverview — site-wide stats page showing aggregate metrics,
 * week-over-week comparison, traffic sources, engagement, and quick wins.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  performanceMetricsService,
  ArticlePerformanceRow,
} from '../../../../services/performanceMetricsService';
import { Sparkline } from './Sparkline';
import { filterByCohort } from './PerformanceDashboard';
import {
  fmt,
  fmtShortDate,
  parseIsoDate,
  weekDatesFromStarts,
  monthDatesFromStarts,
  weekDates,
  calendarMonthDates,
  PeriodBarChart,
  PeriodComparisonTable,
} from './PeriodTrends';

function fmtTime(seconds: number): string {
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  }
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function fmtHours(seconds: number): string {
  const h = Math.round(seconds / 3600);
  return `${h.toLocaleString()} hours`;
}

function sumSeries(rows: ArticlePerformanceRow[], field: 'clicks_series' | 'impressions_series' | 'ga4_sessions_series' | 'ga4_ai_sessions_series'): number[] {
  const maxLen = Math.max(...rows.map(r => ((r as any)[field] || []).length), 0);
  const result: number[] = [];
  for (let i = 0; i < maxLen; i++) {
    let day = 0;
    for (const r of rows) {
      day += ((r as any)[field] || [])[i] || 0;
    }
    result.push(day);
  }
  return result;
}


function MetricCard({ label, value, sub }: {
  label: string; value: string; sub?: string;
}) {
  return (
    <div className="border border-gray-200 rounded-lg px-4 py-3 bg-white">
      <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// Sum weekly series across all articles (element-wise sum)
function sumWeeklySeries(rows: ArticlePerformanceRow[], field: 'weekly_clicks' | 'weekly_impressions' | 'weekly_sessions' | 'weekly_ai_sessions'): number[] {
  const maxLen = Math.max(...rows.map(r => ((r as any)[field] || []).length), 0);
  const result: number[] = [];
  for (let i = 0; i < maxLen; i++) {
    let total = 0;
    for (const r of rows) {
      total += ((r as any)[field] || [])[i] || 0;
    }
    result.push(total);
  }
  return result;
}

// Sum monthly series across all articles (element-wise sum)
function sumMonthlySeries(rows: ArticlePerformanceRow[], field: 'monthly_clicks' | 'monthly_impressions' | 'monthly_sessions' | 'monthly_ai_sessions'): number[] {
  const maxLen = Math.max(...rows.map(r => ((r as any)[field] || []).length), 0);
  const result: number[] = [];
  for (let i = 0; i < maxLen; i++) {
    let total = 0;
    for (const r of rows) {
      total += ((r as any)[field] || [])[i] || 0;
    }
    result.push(total);
  }
  return result;
}

// Traffic source bar chart
function TrafficSources({ rows }: { rows: ArticlePerformanceRow[] }) {
  const channelTotals: Record<string, number> = {};
  for (const r of rows) {
    const ch = (r as any).ga4_top_channel;
    if (ch) {
      channelTotals[ch] = (channelTotals[ch] || 0) + (r.ga4_sessions || 0);
    }
  }
  const sorted = Object.entries(channelTotals).sort((a, b) => b[1] - a[1]);
  const maxVal = sorted.length > 0 ? sorted[0][1] : 1;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-3 cursor-help" title="Source: Google Analytics (GA4). Sessions grouped by channel. AI referral traffic (ChatGPT, Perplexity, etc.) may be included in Referral or Organic Search — it is also reported separately in the AI Referral Traffic panel.">Traffic Sources (by sessions)</div>
      <div className="space-y-2">
        {sorted.slice(0, 8).map(([channel, sessions]) => (
          <div key={channel} className="flex items-center gap-2">
            <div className="w-28 shrink-0 text-xs text-gray-600 truncate text-right">{channel}</div>
            <div className="flex-1 bg-gray-50 rounded-full h-4 overflow-hidden">
              <div
                className="h-full rounded-full bg-purple-500 transition-all duration-500"
                style={{ width: `${(sessions / maxVal) * 100}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-gray-500 w-10 text-right">{fmt(sessions)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// AI traffic breakdown
function AiTraffic({ rows }: { rows: ArticlePerformanceRow[] }) {
  // Aggregate AI sources across all articles
  const sourceTotals: Record<string, number> = {};
  let totalAiSessions = 0;
  for (const r of rows) {
    const ai = (r as any).ga4_ai_sessions || 0;
    totalAiSessions += ai;
    const sources = (r as any).ga4_ai_sources;
    if (sources && typeof sources === 'object') {
      for (const [src, cnt] of Object.entries(sources)) {
        sourceTotals[src] = (sourceTotals[src] || 0) + Number(cnt || 0);
      }
    }
  }

  // Top articles by AI sessions
  const topAiArticles = [...rows]
    .filter(r => ((r as any).ga4_ai_sessions || 0) > 0)
    .sort((a, b) => ((b as any).ga4_ai_sessions || 0) - ((a as any).ga4_ai_sessions || 0))
    .slice(0, 5);

  const sortedSources = Object.entries(sourceTotals).sort((a, b) => b[1] - a[1]);
  const maxSrc = sortedSources.length > 0 ? sortedSources[0][1] : 1;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-3">
        AI Referral Traffic (28d)
      </div>
      {totalAiSessions === 0 ? (
        <div className="text-xs text-gray-400">
          No AI referral sessions detected in the past 28 days.
          <span className="block mt-1 text-gray-300">
            Tracks visits from ChatGPT, Perplexity, Gemini, Claude, Copilot, and other AI tools.
          </span>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-lg font-bold text-gray-900">{totalAiSessions} sessions</div>
          {/* Source breakdown */}
          {sortedSources.length > 0 && (
            <div className="space-y-1.5">
              {sortedSources.map(([src, sessions]) => (
                <div key={src} className="flex items-center gap-2">
                  <div className="w-28 shrink-0 text-[11px] text-gray-600 truncate text-right">{src}</div>
                  <div className="flex-1 bg-gray-50 rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                      style={{ width: `${(sessions / maxSrc) * 100}%` }}
                    />
                  </div>
                  <span className="text-[10px] font-mono text-gray-500 w-8 text-right">{sessions}</span>
                </div>
              ))}
            </div>
          )}
          {/* Top articles from AI */}
          {topAiArticles.length > 0 && (
            <div>
              <div className="text-[10px] text-gray-400 mt-2 mb-1">Top articles from AI</div>
              {topAiArticles.map((r, i) => (
                <div key={r.url} className="flex items-center gap-2 text-[11px]">
                  <span className="text-gray-400 w-3">{i + 1}.</span>
                  <span className="text-gray-700 truncate flex-1" title={r.title}>{r.title}</span>
                  <span className="font-mono text-emerald-600 shrink-0">{(r as any).ga4_ai_sessions}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Quick wins lists
function QuickWins({ rows }: { rows: ArticlePerformanceRow[] }) {
  const topByClicks = [...rows].sort((a, b) => (b.clicks || 0) - (a.clicks || 0)).slice(0, 5);
  const highImprLowClicks = [...rows]
    .filter(r => (r.impressions || 0) > 100 && (r.clicks || 0) < 5)
    .sort((a, b) => (b.impressions || 0) - (a.impressions || 0))
    .slice(0, 5);

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Top Performers (clicks)</div>
        <div className="space-y-1.5">
          {topByClicks.map((r, i) => (
            <div key={r.url} className="flex items-center gap-2 text-xs">
              <span className="text-gray-400 w-4 shrink-0">{i + 1}.</span>
              <span className="text-gray-700 truncate flex-1" title={r.title}>{r.title}</span>
              <span className="font-mono text-gray-500 shrink-0">{fmt(r.clicks)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Opportunities (high impressions, low clicks)</div>
        <div className="space-y-1.5">
          {highImprLowClicks.length === 0 ? (
            <div className="text-xs text-gray-400">No clear opportunities found</div>
          ) : highImprLowClicks.map((r, i) => (
            <div key={r.url} className="flex items-center gap-2 text-xs">
              <span className="text-gray-400 w-4 shrink-0">{i + 1}.</span>
              <span className="text-gray-700 truncate flex-1" title={r.title}>{r.title}</span>
              <span className="font-mono text-amber-600 shrink-0" title={`${fmt(r.impressions)} impr, ${r.clicks} clicks`}>{fmt(r.impressions)} impr</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const PerformanceOverview: React.FC<{ cohort?: import('./PerformanceDashboard').CohortFilter; contentType?: import('../../../../services/performanceMetricsService').ContentTypeFilter }> = ({ cohort = 'all', contentType = 'post' }) => {
  const [allRows, setAllRows] = useState<ArticlePerformanceRow[]>([]);
  const [serverWeeklyPubs, setServerWeeklyPubs] = useState<number[]>([]);
  const [serverMonthlyPubs, setServerMonthlyPubs] = useState<number[]>([]);
  const [seriesMeta, setSeriesMeta] = useState<{
    weeklyStarts: string[];
    monthlyStarts: string[];
    windowStart: string | null;
    dataThrough: string | null;
  }>({ weeklyStarts: [], monthlyStarts: [], windowStart: null, dataThrough: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await performanceMetricsService.list({
          days: 28,
          sortBy: 'clicks',
          order: 'desc',
          limit: 2000,
          contentType,
        });
        if (!cancelled) {
          setAllRows(resp.metrics);
          setServerWeeklyPubs(resp.weekly_publications || []);
          setServerMonthlyPubs(resp.monthly_publications || []);
          setSeriesMeta({
            weeklyStarts: resp.weekly_period_starts || [],
            monthlyStarts: resp.monthly_period_starts || [],
            windowStart: resp.window_start || null,
            dataThrough: resp.data_through || null,
          });
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [contentType]);

  const rows = useMemo(() => filterByCohort(allRows, cohort), [allRows, cohort]);

  const agg = useMemo(() => {
    const impressionsSeries = sumSeries(rows, 'impressions_series');
    const clicksSeries = sumSeries(rows, 'clicks_series');
    const sessionsSeries = sumSeries(rows, 'ga4_sessions_series');
    const aiSessionsSeries = sumSeries(rows, 'ga4_ai_sessions_series');

    const totalImpressions = rows.reduce((s, r) => s + (r.impressions || 0), 0);
    const totalClicks = rows.reduce((s, r) => s + (r.clicks || 0), 0);
    const totalSessions = rows.reduce((s, r) => s + (r.ga4_sessions || 0), 0);
    const totalUsers = rows.reduce((s, r) => s + ((r as any).ga4_total_users || 0), 0);
    const totalEngDuration = rows.reduce((s, r) => s + ((r as any).ga4_engagement_duration || 0), 0);
    const totalScrolled = rows.reduce((s, r) => s + ((r as any).ga4_scrolled_users || 0), 0);
    const avgReadTime = totalUsers > 0 ? totalEngDuration / totalUsers : 0;
    const scrollRate = totalUsers > 0 ? totalScrolled / totalUsers : 0;

    let brWeighted = 0;
    let brSessions = 0;
    for (const r of rows) {
      const sess = r.ga4_sessions || 0;
      const br = r.ga4_bounce_rate;
      if (sess > 0 && br != null) {
        brWeighted += br * sess;
        brSessions += sess;
      }
    }
    const engagementRate = brSessions > 0 ? 1 - (brWeighted / brSessions) : null;

    // AI Overview aggregates
    const totalAioImpressions = rows.reduce((s, r) => s + ((r as any).ai_overview_impressions || 0), 0);
    const totalAioClicks = rows.reduce((s, r) => s + ((r as any).ai_overview_clicks || 0), 0);
    const totalOrganicImpressions = totalImpressions - totalAioImpressions;
    const totalOrganicClicks = totalClicks - totalAioClicks;
    const organicCtr = totalOrganicImpressions > 0 ? totalOrganicClicks / totalOrganicImpressions : null;
    const articlesInAio = rows.filter(r => ((r as any).ai_overview_impressions || 0) > 0).length;

    // Publication series — count articles published per day in the 28-day window
    const today = new Date();
    const pubCounts: Record<string, number> = {};
    for (const r of rows) {
      if (r.published_at) {
        const d = r.published_at.slice(0, 10);
        pubCounts[d] = (pubCounts[d] || 0) + 1;
      }
    }
    const publicationSeries: number[] = [];
    for (let i = 27; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i - 1); // align with the metrics window (yesterday back 28d)
      const iso = d.toISOString().slice(0, 10);
      publicationSeries.push(pubCounts[iso] || 0);
    }
    const articlesPublished28d = publicationSeries.reduce((s, v) => s + v, 0);

    // 52-week series (pre-aggregated by backend)
    const weeklyImpressions = sumWeeklySeries(rows, 'weekly_impressions');
    const weeklyClicks = sumWeeklySeries(rows, 'weekly_clicks');
    const weeklySessions = sumWeeklySeries(rows, 'weekly_sessions');
    const weeklyAiSessions = sumWeeklySeries(rows, 'weekly_ai_sessions');

    // Publications — use server-computed series (accurate, no timezone issues)
    const weeklyPublications = serverWeeklyPubs;

    // Calendar-month series (from backend per-article data, respects cohort filter)
    const monthlyImpressions = sumMonthlySeries(rows, 'monthly_impressions');
    const monthlyClicks = sumMonthlySeries(rows, 'monthly_clicks');
    const monthlySessions = sumMonthlySeries(rows, 'monthly_sessions');

    return {
      impressionsSeries,
      clicksSeries,
      sessionsSeries,
      publicationSeries,
      articlesPublished28d,
      totalImpressions,
      totalClicks,
      totalSessions,
      totalUsers,
      totalEngDuration,
      avgReadTime,
      scrollRate,
      engagementRate,
      totalAioImpressions,
      totalAioClicks,
      totalOrganicImpressions,
      totalOrganicClicks,
      organicCtr,
      articlesInAio,
      aiSessionsSeries,
      weeklyImpressions,
      weeklyClicks,
      weeklySessions,
      weeklyAiSessions,
      weeklyPublications,
      // Calendar-month aggregates
      monthlyImpressions,
      monthlyClicks,
      monthlySessions,
      monthlyPublications: serverMonthlyPubs.length > 0 ? serverMonthlyPubs : [],
    };
  }, [rows, serverWeeklyPubs, serverMonthlyPubs]);

  // Period boundaries — server-provided starts are the source of truth
  // (aligned with the backend's series buckets); client-side derivation is
  // only a fallback for snapshots built before period starts were added.
  const weeklyDates = useMemo(() =>
    seriesMeta.weeklyStarts.length > 0
      ? weekDatesFromStarts(seriesMeta.weeklyStarts)
      : weekDates(Math.max(agg.weeklyImpressions.length, agg.weeklyPublications.length)),
    [seriesMeta.weeklyStarts, agg.weeklyImpressions.length, agg.weeklyPublications.length]);
  const monthlyDates = useMemo(() =>
    seriesMeta.monthlyStarts.length > 0
      ? monthDatesFromStarts(seriesMeta.monthlyStarts)
      : calendarMonthDates(Math.max(agg.monthlyImpressions.length, agg.monthlyPublications.length)),
    [seriesMeta.monthlyStarts, agg.monthlyImpressions.length, agg.monthlyPublications.length]);

  // Comparison indices: last complete period vs the one before it; the final
  // series entry is the current in-progress period (footnote only)
  const wl = weeklyDates.length;
  const ml = monthlyDates.length;
  const weekLast = weeklyDates[wl - 2];
  const weekPrior = weeklyDates[wl - 3];
  const weekInProg = weeklyDates[wl - 1];
  const monthLast = monthlyDates[ml - 2];
  const monthPrior = monthlyDates[ml - 3];
  const monthInProg = monthlyDates[ml - 1];

  const windowLabel = seriesMeta.windowStart && seriesMeta.dataThrough
    ? `${fmtShortDate(parseIsoDate(seriesMeta.windowStart))} – ${fmtShortDate(parseIsoDate(seriesMeta.dataThrough))}`
    : 'last 28 days';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500" />
      </div>
    );
  }

  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>;
  }

  return (
    <div className="space-y-4">
      {seriesMeta.dataThrough && (
        <div className="text-[11px] text-gray-400 text-right">
          Search data through <span className="font-medium text-gray-500">{fmtShortDate(parseIsoDate(seriesMeta.dataThrough))}</span> · GSC lags ~2–3 days
        </div>
      )}

      {/* Big number cards */}
      <div className="grid grid-cols-5 gap-4">
        <MetricCard label="Articles" value={String(rows.length)} />
        <MetricCard label="Impressions" value={fmt(agg.totalImpressions)} sub={windowLabel} />
        <MetricCard label="Clicks" value={fmt(agg.totalClicks)} sub={windowLabel} />
        <MetricCard label="Sessions" value={fmt(agg.totalSessions)} sub={windowLabel} />
        <MetricCard label="Unique Users" value={fmt(agg.totalUsers)} sub={windowLabel} />
      </div>

      {/* Engagement */}
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-3">Engagement (28d)</div>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-400">Total Reading Time</div>
            <div className="text-lg font-bold text-gray-900">{fmtHours(agg.totalEngDuration)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Avg Read Time / Visit</div>
            <div className="text-lg font-bold text-gray-900">{fmtTime(agg.avgReadTime)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Scroll Depth (90%)</div>
            <div className="text-lg font-bold text-gray-900">{(agg.scrollRate * 100).toFixed(1)}%</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Engagement Rate</div>
            <div className="text-lg font-bold text-gray-900">{agg.engagementRate != null ? `${(agg.engagementRate * 100).toFixed(1)}%` : '—'}</div>
          </div>
        </div>
      </div>

      {/* AI Overview summary */}
      {agg.totalAioImpressions > 0 && (
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-3">Google AI Overview Impact (28d)</div>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-gray-400">AI Overview Impressions</div>
              <div className="text-lg font-bold text-gray-900">{fmt(agg.totalAioImpressions)}</div>
              <div className="text-[10px] text-gray-400">{(agg.totalAioImpressions / Math.max(agg.totalImpressions, 1) * 100).toFixed(1)}% of all impressions</div>
            </div>
            <div>
              <div className="text-xs text-gray-400">AI Overview Clicks</div>
              <div className="text-lg font-bold text-gray-900">{fmt(agg.totalAioClicks)}</div>
              <div className="text-[10px] text-gray-400">{agg.totalAioImpressions > 0 ? `${(agg.totalAioClicks / agg.totalAioImpressions * 100).toFixed(2)}% CTR` : ''}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Organic CTR (excl. AI)</div>
              <div className="text-lg font-bold text-gray-900">{agg.organicCtr != null ? `${(agg.organicCtr * 100).toFixed(2)}%` : '—'}</div>
              <div className="text-[10px] text-gray-400">vs {(agg.totalClicks / Math.max(agg.totalImpressions, 1) * 100).toFixed(2)}% overall</div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Articles in AI Overviews</div>
              <div className="text-lg font-bold text-gray-900">{agg.articlesInAio}</div>
              <div className="text-[10px] text-gray-400">of {rows.length} articles</div>
            </div>
          </div>
        </div>
      )}

      {/* ── MONTHLY ── */}
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-1 mt-2">Monthly</div>

      <PeriodComparisonTable
        title="Month over Month"
        currentLabel="Last Month"
        currentSub={monthLast ? monthLast.start.toLocaleDateString(undefined, { month: 'long' }) : ''}
        previousLabel="Prior Month"
        previousSub={monthPrior ? monthPrior.start.toLocaleDateString(undefined, { month: 'long' }) : ''}
        inProgressNote={`${monthInProg ? monthInProg.start.toLocaleDateString(undefined, { month: 'long' }) : 'This month'} so far (partial — GSC lags ~2–3 days)`}
        metrics={[
          { label: 'Impressions', current: agg.monthlyImpressions[ml - 2] || 0, previous: agg.monthlyImpressions[ml - 3] || 0, inProgress: agg.monthlyImpressions[ml - 1] || 0 },
          { label: 'Clicks', current: agg.monthlyClicks[ml - 2] || 0, previous: agg.monthlyClicks[ml - 3] || 0, inProgress: agg.monthlyClicks[ml - 1] || 0 },
          { label: 'Sessions', current: agg.monthlySessions[ml - 2] || 0, previous: agg.monthlySessions[ml - 3] || 0, inProgress: agg.monthlySessions[ml - 1] || 0 },
        ]}
      />

      <div className="grid grid-cols-4 gap-4">
        <PeriodBarChart values={agg.monthlyImpressions} color="#01bcd4" label="Monthly Impressions" periodLabel="last month" dates={monthlyDates} />
        <PeriodBarChart values={agg.monthlyClicks} color="#16a34a" label="Monthly Clicks" periodLabel="last month" dates={monthlyDates} />
        <PeriodBarChart values={agg.monthlySessions} color="#8b5cf6" label="Monthly Sessions" periodLabel="last month" dates={monthlyDates} />
        <PeriodBarChart values={agg.monthlyPublications} color="#f59e0b" label="Monthly Published" periodLabel="last month" dates={monthlyDates} />
      </div>

      {/* ── WEEKLY ── */}
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-1 mt-2">Weekly</div>

      <PeriodComparisonTable
        title="Week over Week"
        currentLabel="Last Week"
        currentSub={weekLast ? `${fmtShortDate(weekLast.start)} – ${fmtShortDate(weekLast.end)}` : ''}
        previousLabel="Prior Week"
        previousSub={weekPrior ? `${fmtShortDate(weekPrior.start)} – ${fmtShortDate(weekPrior.end)}` : ''}
        inProgressNote={`Week in progress${weekInProg ? ` (since ${fmtShortDate(weekInProg.start)})` : ''} · partial — GSC lags ~2–3 days`}
        metrics={[
          { label: 'Impressions', current: agg.weeklyImpressions[wl - 2] || 0, previous: agg.weeklyImpressions[wl - 3] || 0, inProgress: agg.weeklyImpressions[wl - 1] || 0 },
          { label: 'Clicks', current: agg.weeklyClicks[wl - 2] || 0, previous: agg.weeklyClicks[wl - 3] || 0, inProgress: agg.weeklyClicks[wl - 1] || 0 },
          { label: 'Sessions', current: agg.weeklySessions[wl - 2] || 0, previous: agg.weeklySessions[wl - 3] || 0, inProgress: agg.weeklySessions[wl - 1] || 0 },
        ]}
      />

      <div className="grid grid-cols-4 gap-4">
        <PeriodBarChart values={agg.weeklyImpressions} color="#01bcd4" label="Weekly Impressions" periodLabel="last week" dates={weeklyDates} />
        <PeriodBarChart values={agg.weeklyClicks} color="#16a34a" label="Weekly Clicks" periodLabel="last week" dates={weeklyDates} />
        <PeriodBarChart values={agg.weeklySessions} color="#8b5cf6" label="Weekly Sessions" periodLabel="last week" dates={weeklyDates} />
        <PeriodBarChart values={agg.weeklyPublications} color="#f59e0b" label="Weekly Published" periodLabel="last week" dates={weeklyDates} />
      </div>

      {/* ── DAILY ── */}
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-1 mt-2">Daily (last 28 days)</div>

      <div className="grid grid-cols-4 gap-4">
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Daily Impressions</div>
          <div className="[&_svg]:w-full"><Sparkline values={agg.impressionsSeries} width={600} height={80} /></div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Daily Clicks</div>
          <div className="[&_svg]:w-full"><Sparkline values={agg.clicksSeries} width={600} height={80} strokeColor="#16a34a" fillColor="rgba(22, 163, 74, 0.15)" /></div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Daily Sessions</div>
          <div className="[&_svg]:w-full"><Sparkline values={agg.sessionsSeries} width={600} height={80} strokeColor="#8b5cf6" fillColor="rgba(139, 92, 246, 0.15)" /></div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Daily Publications · {agg.articlesPublished28d} new</div>
          <div className="[&_svg]:w-full"><Sparkline values={agg.publicationSeries} width={600} height={80} strokeColor="#f59e0b" fillColor="rgba(245, 158, 11, 0.15)" /></div>
        </div>
      </div>

      {/* Traffic sources + AI visibility + Quick wins */}
      <div className="grid grid-cols-2 gap-4">
        <TrafficSources rows={rows} />
        <div className="space-y-4">
          <AiTraffic rows={rows} />
          {(agg.weeklyAiSessions || []).some((v: number) => v > 0) && (
            <div className="grid grid-cols-2 gap-4">
              <PeriodBarChart values={agg.weeklyAiSessions} color="#10b981" label="Weekly AI Sessions" periodLabel="last week" dates={weeklyDates} />
              <div className="border border-gray-200 rounded-lg p-4 bg-white">
                <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Daily AI Sessions (28d)</div>
                <div className="[&_svg]:w-full"><Sparkline values={agg.aiSessionsSeries} width={600} height={64} strokeColor="#10b981" fillColor="rgba(16, 185, 129, 0.15)" /></div>
              </div>
            </div>
          )}
        </div>
      </div>
      <QuickWins rows={rows} />

      <div className="text-[10px] text-gray-300 text-right">{rows.length} articles · 28-day window ({windowLabel})</div>
    </div>
  );
};
